import { z } from 'zod';
const DELIMITER = '~';
/** User-facing operator → wire operator (the wire operator is also the Prisma one). */
const OPERATORS = {
    exact: 'eq',
    contains: 'contains',
    greaterThan: 'gt',
    greaterOrEqual: 'gte',
    lesserThan: 'lt',
    lesserOrEqual: 'lte',
    before: 'lt',
    after: 'gt',
};
const WIRE_OPERATORS = [
    ...new Set(Object.values(OPERATORS)),
];
const FILTER_VALUE_TYPES = ['string', 'number', 'boolean', 'date'];
/**
 * A single decoded filter: exactly one wire operator holding the raw string
 * value, plus the value type (resolved from the filter schema) used by the
 * consumer to coerce it, e.g. `{ gt: '18', type: 'number' }`.
 */
const ComplexFilterDecodedSchema = z.object({
    eq: z.string().optional(),
    contains: z.string().optional(),
    gt: z.string().optional(),
    gte: z.string().optional(),
    lt: z.string().optional(),
    lte: z.string().optional(),
    type: z.enum(FILTER_VALUE_TYPES),
});
function resolveFilterValueType(schema) {
    if (schema instanceof z.ZodOptional ||
        schema instanceof z.ZodNullable ||
        schema instanceof z.ZodDefault) {
        return resolveFilterValueType(schema.unwrap());
    }
    if (schema instanceof z.ZodNumber) {
        return 'number';
    }
    if (schema instanceof z.ZodBoolean) {
        return 'boolean';
    }
    if (schema instanceof z.ZodDate ||
        (schema instanceof z.ZodString && schema.format === 'datetime')) {
        return 'date';
    }
    return 'string';
}
function encodeWire(operator, value) {
    return `${DELIMITER}${operator}${DELIMITER}${value}`;
}
function decodeWire(encoded, type) {
    const [prefix, operator, ...value] = encoded.split(DELIMITER);
    if (prefix !== '' ||
        !operator ||
        !WIRE_OPERATORS.includes(operator)) {
        throw new Error(`Invalid complex filter: ${encoded}`);
    }
    return { [operator]: value.join(DELIMITER), type };
}
function encodeDecodedFilter(key, filter) {
    for (const operator of WIRE_OPERATORS) {
        const value = filter[operator];
        if (value !== undefined) {
            return encodeWire(operator, value);
        }
    }
    throw new Error(`Missing operator for filter "${key}"`);
}
/** Encodes a user filter (`{ contains: 'x' }`, or a bare value for `exact`); `undefined` when empty. */
function encodeUserFilter(input) {
    const [operator, value] = typeof input === 'object' && input !== null && !(input instanceof Date)
        ? (Object.entries(input).find(([, v]) => v != null && v !== '') ??
            [])
        : ['exact', input];
    if (operator === undefined || value == null || value === '') {
        return undefined;
    }
    return encodeWire(OPERATORS[operator], value instanceof Date ? value.toISOString() : String(value));
}
/**
 * Builds the complex filters for a set of fields.
 *
 * - `codec` is passed as `filters` to `createListQuery()`. On the wire every
 *   filter is a `~<op>~<value>` string; after parsing it is a
 *   {@link ComplexFilterDecoded}.
 * - `encode()` turns the user-friendly {@link ComplexFiltersInput} into the
 *   wire form expected by `listQuery.encode({ filters })`.
 */
export function complexFilters(schema) {
    const shape = schema.shape;
    const typeOf = {};
    const wireShape = {};
    const outputShape = {};
    for (const key in shape) {
        typeOf[key] = resolveFilterValueType(shape[key]);
        wireShape[key] = z.string().optional();
        outputShape[key] = ComplexFilterDecodedSchema.optional();
    }
    const wireSchema = z.object(wireShape);
    const outputSchema = z.object(outputShape);
    const codec = z.codec(wireSchema, outputSchema, {
        decode: (wire) => {
            const decoded = {};
            for (const key in wire) {
                const value = wire[key];
                if (value !== undefined) {
                    decoded[key] = decodeWire(value, typeOf[key] ?? 'string');
                }
            }
            return decoded;
        },
        encode: (decoded) => {
            const wire = {};
            for (const key in decoded) {
                const filter = decoded[key];
                if (filter !== undefined) {
                    wire[key] = encodeDecodedFilter(key, filter);
                }
            }
            return wire;
        },
    });
    return {
        codec,
        encode: (filters) => {
            const wire = {};
            for (const key in filters) {
                const input = filters[key];
                const encoded = encodeUserFilter(input);
                if (encoded !== undefined) {
                    wire[key] = encoded;
                }
            }
            return wire;
        },
    };
}
